// HTTP Client with Authorization support
// Handles API calls to the backend
// IMPORTANT: Uses runtime API base URL detection

import { getApiBaseUrl } from './config';

class ApiClient {
  constructor() {
    // DO NOT cache baseUrl at construction time
    // Use getter to get runtime value
    this.token = null;
  }

  // Get API base URL at runtime (not cached)
  getBaseUrl() {
    return getApiBaseUrl();
  }

  // Set auth token (call after login)
  setToken(token) {
    this.token = token;
  }

  // Clear auth token (call on logout)
  clearToken() {
    this.token = null;
  }

  // Get stored token
  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  // Build headers with optional auth
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  // Generic request method
  async request(method, path, options = {}) {
    // Get base URL at request time (runtime)
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${path}`;
    const { body, auth = true, ...fetchOptions } = options;

    console.log(`[API Client] ${method} ${url}`);

    const response = await fetch(url, {
      method,
      headers: this.getHeaders(auth),
      body: body ? JSON.stringify(body) : undefined,
      ...fetchOptions,
    });

    // Parse JSON response
    const data = await response.json().catch(() => ({}));

    // Handle error responses
    if (!response.ok) {
      const error = new Error(data.error?.message || `Request failed: ${response.status}`);
      error.status = response.status;
      error.code = data.error?.code;
      error.data = data;
      throw error;
    }

    return data;
  }

  // Convenience methods
  get(path, options) {
    return this.request('GET', path, options);
  }

  post(path, body, options) {
    return this.request('POST', path, { body, ...options });
  }

  patch(path, body, options) {
    return this.request('PATCH', path, { body, ...options });
  }

  put(path, body, options) {
    return this.request('PUT', path, { body, ...options });
  }

  delete(path, options) {
    return this.request('DELETE', path, options);
  }
}

// Singleton instance
export const apiClient = new ApiClient();

export default apiClient;
